<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12" ref="loader">
                <h1>{{categoryName}}</h1>
                <div class="row">
                    <div class="wos__content container-fluid bg-white shadow-sm">
                        <section class="mb-5">
                            <div class="row mt-4 border-bottom">
                                <div class="col-sm-12">
                                    <div class="row">
                                        <div class="col-sm-12 col-md-5">
                                            {{currentFetchStatus}}
                                            <button class="btn btn-sm btn-outline-wellis-black ml-3 mb-2" @click="fetchAll" v-if="hasMore">
                                                {{$t('components.products.category.btnFetchAll')}}
                                            </button>
                                        </div>
                                        <div class="col-sm-12 col-md-7">
                                            <div class="float-right">
                                                <label class="noselect">{{$t('components.products.category.sort.title')}}:
                                                    <select class="invisible-select" style="font-size:13px;" v-model="sortVal">
                                                        <option value="nameAsc">{{$t('components.products.category.sort.types.nameAsc')}}</option>
                                                        <option value="nameDesc">{{$t('components.products.category.sort.types.nameDesc')}}</option>
                                                        <option value="priceAsc">{{$t('components.products.category.sort.types.priceAsc')}}</option>
                                                        <option value="priceDesc">{{$t('components.products.category.sort.types.priceDesc')}}</option>
                                                    </select>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-sm-12 p-0 d-flex col-lg-3" v-for="(product, index) in products" :key="index">
                                    <ProductCard :product="product" :category-slug="fullSlug"/>
                                </div>
                            </div>
                            <div class="row mt-5" v-if="hasMore">
                                <div class="col-sm-12 text-center">
                                    <button class="btn btn-wos-real col-sm-12 col-md-3" @click="fetch">
                                        {{$t('components.products.category.btnShowMore')}}
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ProductApiService from "../../../api/product.api";
    import ProductCard from "../ProductCard";
    import {__clone} from "../../../helpers";
    import {EVENT_LANG_CHANGE} from "../../../core/constants";

    const DEFAULT_FILTERS = {
        orderBy: 'name',
        orderType: 'asc',
        offset: 0,
    };

    const DEFAULT_SORT = 'nameAsc';

    export default {
        name: "ProductCategory",
        components: {ProductCard},
        mounted() {
            this.bootstrap();

            this.$eventBus.$on(EVENT_LANG_CHANGE, () => {
                this.$router.push({name: 'app.start'});
            });
        },

        destroyed() {
            this.$eventBus.$off(EVENT_LANG_CHANGE);
        },

        data() {
            return {
                loading: null,
                slug: null,
                category: null,
                items: [],
                hasMore: false,
                filters: __clone(DEFAULT_FILTERS),
                sortVal: __clone(DEFAULT_SORT)
            }
        },

        methods: {
            bootstrap() {
                this.hideLoader();
                this.slug = this.$route.params.category.split('/').pop();
                this.category = null;
                this.items = [];
                this.totalItems = 0;
                this.hasMore = false;
                this.$set(this, 'filters', __clone(DEFAULT_FILTERS));
                this.$set(this, 'sortVal', __clone(DEFAULT_SORT));
                this.fetch();
            },

            fetch() {
                this.showLoader();
                ProductApiService.getProducts(this.slug, this.filters.orderBy, this.filters.orderType, this.filters.offset)
                    .then(response => {
                        this.filters.offset = response.data.currentOffset;
                        this.filters.orderBy = response.data.orderBy;
                        this.filters.orderType = response.data.orderType;
                        this.totalItems = response.data.total;
                        this.category = response.data.category;
                        this.items = this.items.concat(response.data.products);
                        this.hasMore = response.data.hasMore;
                        this.applySort();
                    })
                    .catch(() => {
                        return this.$router.push({name: 'app.404'});
                    })
                    .finally(() => this.hideLoader());
            },

            fetchAll() {
                this.hasMore = false;
                this.showLoader();
                this.items = [];
                ProductApiService.getProductsUnlimited(this.slug)
                    .then(response => {
                        this.filters.offset = response.data.total;
                        this.filters.orderBy = response.data.orderBy;
                        this.filters.orderType = response.data.orderType;
                        this.totalItems = response.data.total;
                        this.category = response.data.category;
                        this.items = this.items.concat(response.data.products);
                        this.hasMore = response.data.hasMore;
                        this.applySort();
                    })
                    .catch(() => {
                        return this.$router.push({name: 'app.404'});
                    })
                    .finally(() => this.hideLoader());
            },

            showLoader() {
                return new Promise(resolve => {
                    this.hideLoader().then(() => {
                        this.loading = this.$loading.show({
                            container: this.$refs.loader,
                            opacity: 0.5
                        });
                        resolve();
                    });
                });
            },

            hideLoader() {
                return new Promise(resolve => {
                    if (this.loading) {
                        this.loading.hide();
                        this.loading = null;
                    }
                    resolve();
                });
            },

            applySort() {
                let items = __clone(this.items);
                let currentSort = this.sortVal;
                this.$set(this, 'items', items.sort(function (a, b) {
                    switch (currentSort) {
                        case 'nameAsc':
                            return a.name > b.name;
                        case 'nameDesc':
                            return a.name < b.name;
                        case 'priceAsc':
                            if (a.price === null) {
                                return false;
                            }
                            return a.price.unitPrice > b.price.unitPrice;
                        case 'priceDesc':
                            if (a.price === null) {
                                return false;
                            }
                            return a.price.unitPrice < b.price.unitPrice;
                    }
                }));
            }
        },

        computed: {
            categoryName() {
                if (!this.category) {
                    return '-';
                }
                return this.category.name;
            },

            currentFetchStatus() {
                return `${this.items.length} / ${this.totalItems} ${this.$t('components.products.category.items')}`
            },

            products() {
                return this.items;
            },

            fullSlug() {
                return this.$route.params.category;
            }
        },

        watch: {
            '$route.params.category': function () {
                this.bootstrap();
            },

            'sortVal': function (val) {
                switch (val) {
                    case 'nameDesc':
                        this.filters.orderBy = 'name';
                        this.filters.orderType = 'desc';
                        break;

                    case 'priceAsc':
                        this.filters.orderBy = 'price';
                        this.filters.orderType = 'asc';
                        break;

                    case 'priceDesc':
                        this.filters.orderBy = 'price';
                        this.filters.orderType = 'desc';
                        break;

                    case 'nameAsc':
                    default:
                        this.filters.orderBy = 'name';
                        this.filters.orderType = 'asc';
                        break;
                }

                this.items = [];
                this.filters.offset = 0;
                this.fetch();
            }
        }
    }
</script>

<style scoped>

</style>
